import { format } from "date-fns";
import { de, enUS, ja } from "date-fns/locale";
import { isDateOverdue } from "@/lib/dateUtils";
import type { InspectionWithProfiles, Defect, DashboardStats } from "@/hooks/useDashboardQuery";

/**
 * Reine Helfer/Ableitungen fuer das DashboardBento (S1, TES-954).
 *
 * Alle Werte werden aus dem REALEN Schema (`inspection.status`, `defect.*`,
 * `stats.*`) abgeleitet — KEIN neuer Enum, KEINE erfundene Entitaet. Geteilt
 * zwischen Desktop- und Mobile-Layout, damit beide identisch ableiten (keine
 * Duplikation der Logik).
 */

export type InspectionDisplayStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "overdue"
  | "cancelled";

export type GetDisplayStatus = (inspection: {
  status: string;
  due_date: string | null;
}) => InspectionDisplayStatus;

const dateLocales = { de, en: enUS, ja } as const;

export function resolveDateLocale(language: string) {
  return dateLocales[language as keyof typeof dateLocales] ?? de;
}

/** Stabile Kurz-ID aus der Pruefungs-UUID (#INS-xxxxxx, rein praesentational). */
export function shortInspectionId(id: string): string {
  const compact = id.replace(/-/g, "").slice(0, 6).toUpperCase();
  return `#INS-${compact}`;
}

/** Erster nicht-leerer String aus einer Kandidatenliste (sonst Fallback). */
function firstNonEmpty(candidates: Array<string | undefined>, fallback: string): string {
  const hit = candidates.find((value) => Boolean(value));
  return hit ?? fallback;
}

/** Anzeige-Label einer Pruefung (Position-Name + Checklisten-Fallback). */
export function inspectionAssetName(
  inspection: Pick<InspectionWithProfiles, "inspection_positions" | "checklists">,
  unknownLabel: string,
): string {
  return firstNonEmpty(
    [inspection.inspection_positions?.name, inspection.checklists?.name],
    unknownLabel,
  );
}

/** Mock Pass/Fail/Incomplete-Dot abgeleitet aus realem Inspektions-Anzeige-Status. */
export function statusToDot(status: InspectionDisplayStatus): {
  state: "pass" | "fail" | "scheduled";
  resultKey: "resultPass" | "resultFail" | "resultIncomplete";
} {
  if (status === "completed") return { state: "pass", resultKey: "resultPass" };
  if (status === "overdue") return { state: "fail", resultKey: "resultFail" };
  return { state: "scheduled", resultKey: "resultIncomplete" };
}

const isActiveDefect = (d: Defect) => d.status !== "closed" && d.status !== "cancelled";

/** Aktiver, ueberfaelliger Mangel (Top-Prioritaet im Open-Defects-Panel). */
const isActiveOverdueDefect = (d: Defect) => isActiveDefect(d) && isDateOverdue(d.due_date);

/** Aktiver, nicht-ueberfaelliger Mangel (Open-Bucket ohne Overdue). */
const isActiveOpenDefect = (d: Defect) => isActiveDefect(d) && !isDateOverdue(d.due_date);

/** Offene, nicht abgeschlossene Pruefung (Quelle der MPP-Weekly-Karten). */
const isOpenInspection = (i: InspectionWithProfiles) =>
  i.status !== "completed" && i.status !== "cancelled";

/** Abschluss-Quote in Prozent aus realen stats (kein neuer Status). */
function computeResolutionRate(stats: DashboardStats | null | undefined): number {
  if (!stats || stats.totalDefects <= 0) return 0;
  return Math.round((stats.defectsCompleted / stats.totalDefects) * 100);
}

/** Alle abgeleiteten Anzeige-Werte des Bento an einer Stelle (kein doppeltes Rechnen). */
export interface BentoView {
  overdueDefects: number;
  dueTodayDefects: number;
  resolutionRate: number;
  overdueDefect?: Defect;
  dueTodayDefect?: Defect;
  mppInspections: InspectionWithProfiles[];
}

export function buildBentoView(
  recentInspections: InspectionWithProfiles[],
  criticalDefects: Defect[],
  stats: DashboardStats | null | undefined,
  defectsOpen: number,
): BentoView {
  const overdueDefects = stats?.defectsOverdue ?? 0;
  // "Aktiv offen" = offene Maengel ohne die bereits ueberfaelligen (abgeleitet aus stats,
  // kein neuer Status). Bewusst NICHT "heute faellig" benannt — stats liefert kein
  // tagesscharfes Due-Today-Aggregat; der Wert ist der nicht-ueberfaellige Open-Bucket.
  const dueTodayDefects = Math.max(defectsOpen - overdueDefects, 0);

  return {
    overdueDefects,
    dueTodayDefects,
    resolutionRate: computeResolutionRate(stats),
    overdueDefect: criticalDefects.find(isActiveOverdueDefect),
    dueTodayDefect: criticalDefects.find(isActiveOpenDefect),
    mppInspections: recentInspections.filter(isOpenInspection).slice(0, 4),
  };
}

/**
 * Praesentations-Klassen einer MPP-Karte (Desktop), abgeleitet aus `urgent`.
 * Buendelt alle urgent-abhaengigen Klassen-Entscheidungen an einer Stelle, damit
 * die JSX-Komponente keine verstreuten Ternaries traegt (haelt die Komponenten-
 * Komplexitaet niedrig).
 */
export interface MppCardClasses {
  container: string;
  iconWrap: string;
  badge: string;
  cta: string;
}

export function mppCardClasses(urgent: boolean): MppCardClasses {
  if (urgent) {
    return {
      container: "border-secondary bg-secondary/5",
      iconWrap: "bg-secondary text-secondary-foreground",
      badge: "bg-secondary text-secondary-foreground font-bold",
      cta: "opacity-100",
    };
  }
  return {
    container: "hover:border-primary",
    iconWrap: "bg-surface-container-high text-primary",
    badge: "bg-surface-container-low text-muted-foreground",
    cta: "opacity-0 group-hover:opacity-100",
  };
}

/** Praesentations-Klassen einer mobilen MPP-Scroll-Karte, abgeleitet aus `urgent`. */
export interface MobileMppClasses {
  container: string;
  badge: string;
}

export function mobileMppClasses(urgent: boolean): MobileMppClasses {
  if (urgent) {
    return {
      container: "border-secondary bg-secondary/5",
      badge: "bg-secondary text-secondary-foreground",
    };
  }
  return {
    container: "",
    badge: "bg-primary text-primary-foreground",
  };
}

/** Pill-Klassen einer Recent-Inspection-Karte (Mobile) aus dem Anzeige-Status. */
export function recentPillClasses(status: InspectionDisplayStatus): string {
  if (status === "completed") return "bg-primary-fixed text-primary";
  if (status === "overdue") return "bg-destructive text-destructive-foreground";
  return "bg-surface-container-high text-muted-foreground";
}

export function makeFormatters(language: string) {
  const locale = resolveDateLocale(language);
  return {
    formatTimestamp: (iso: string | null) =>
      iso ? format(new Date(iso), "d. MMM, HH:mm", { locale }) : "—",
    formatDueChip: (iso: string | null, fallback: string) =>
      iso ? format(new Date(iso), "EEE, d. MMM", { locale }).toUpperCase() : fallback,
  };
}
